import React, { useState } from 'react';
import mixpanel from 'mixpanel-browser';
import { FunnelIcon } from '@heroicons/react/20/solid';

import { Button, CheckboxInput, Drawer, InputGroup, UItils } from '@nearst/ui';

import { useDeliveryChannels, useEnabledChannels } from '@services/channels';
import { useShop } from '@services/ShopProvider';
import { availabilityFilters, defaultFilters, statusFilters, useInventory } from '@services/stock';

import plurify from '@utils/plurify';

import { CategorySelect } from '../../InventoryItem/ProductCategory';
import DeliveryCategoriesSelect from './DeliveryCategoriesSelect';

import styles from './InventoryFilters.module.scss';

const countFilters = (filters) => Object.values(filters).filter(Boolean).length;

const FilterToggle = ({ label, checked, onChange }) => {
	return (
		<button onClick={() => onChange(!checked)} className={UItils.classnames(styles.filterToggle, checked && styles.filterToggleActive)}>
			{label}
		</button>
	);
};

const InventoryFilters = () => {
	const { shop, organisation } = useShop();
	const { filters, applyFilters } = useInventory(organisation.id, shop.id);
	const channels = useEnabledChannels();

	const lastMileChannels = useDeliveryChannels();

	const [showFilters, setShowFilters] = useState(false);
	const [filterCount, setFilterCount] = useState(countFilters(filters));

	const defaultCategoryFilter = Object.entries(filters).find(([key, value]) => key.startsWith('category_ids:=') && value);
	const defaultCategoryFilterValue = defaultCategoryFilter?.[0].split('=')[1];
	const [selectedCategory, setSelectedCategory] = useState(defaultCategoryFilterValue);

	const defaultDeliveryFilters = Object.entries(filters).filter(([key, value]) => key.startsWith('delivery_categories:=') && value);
	const defaultDeliveryFiltersValue = defaultDeliveryFilters.map(([key]) => key.split('=')[1]);
	const [selectedDeliveryCategories, setSelectedDeliveryCategories] = useState(defaultDeliveryFiltersValue);

	const apply = (newFilters) => {
		applyFilters(newFilters);
		const numFilterSelected = countFilters(newFilters);
		setFilterCount(numFilterSelected);
	};

	const handleFilterChange = (event) => {
		apply({ ...filters, [event.target.name]: event.target.checked });
	};

	const handleOnReset = () => {
		setSelectedCategory(undefined);
		setSelectedDeliveryCategories([]);
		apply({ ...defaultFilters });
	};

	const track = (filterCategory, filterName) => {
		mixpanel.track('Inventory filter applied', {
			'Filter category': filterCategory,
			'Filter name': filterName
		});
	};

	const filtersDescription = `${filterCount} ${plurify('filter', filterCount)}`;
	const buttonText = filterCount ? filtersDescription : 'Filters';

	return (
		<>
			<Button primary onClick={() => setShowFilters(true)} className={styles.button} data-cy="inventory-filter-toggle">
				<FunnelIcon />
				{buttonText}
			</Button>

			<Drawer open={showFilters} onOpenChange={setShowFilters} data-cy="inventory-filters">
				<Drawer.Header>
					<Drawer.Title>Filters</Drawer.Title>
					<Drawer.Close />
				</Drawer.Header>

				{filterCount > 0 && (
					<div className={styles.subtitle}>
						{filtersDescription} applied
						<Button link className={styles.reset} onClick={handleOnReset}>
							Reset
						</Button>
					</div>
				)}

				<InputGroup label="Availability">
					{availabilityFilters.map((filter) => (
						<CheckboxInput
							key={filter.value}
							label={filter.label}
							name={filter.value}
							checked={filters[filter.value]}
							onChange={handleFilterChange}
							onClick={() => track('Availability', filter.label)}
						/>
					))}
				</InputGroup>

				<InputGroup label="Status">
					{statusFilters.map((filter) => (
						<CheckboxInput
							key={filter.value}
							label={filter.label}
							name={filter.value}
							checked={filters[filter.value]}
							onChange={handleFilterChange}
							onClick={() => track('Status', filter.label)}
						/>
					))}
				</InputGroup>

				<InputGroup label="Channels">
					<div className={styles.filterToggles}>
						{channels
							?.filter(({ id }) => id !== 'shop-page')
							.map((channel) => {
								const id = `channels:=${channel.id}`;
								return (
									<FilterToggle
										key={id}
										label={channel.label}
										checked={filters[id]}
										onChange={(checked) => {
											apply({ ...filters, [id]: checked });
											checked && track('Channels', channel.label);
										}}
									/>
								);
							})}
					</div>
				</InputGroup>

				<InputGroup label="Product category">
					<CategorySelect
						isClearable
						defaultCategory={selectedCategory?.label}
						defaultId={selectedCategory?.id}
						onCategorySelect={(updatedCategory) => {
							const otherFilters = Object.entries(filters).reduce((acc, [key, value]) => {
								if (key.startsWith('category_ids:=')) return acc;
								return { ...acc, [key]: value };
							}, {});

							if (!updatedCategory?.value) {
								setSelectedCategory(undefined);
								apply(otherFilters);
							} else {
								setSelectedCategory({
									value: updatedCategory.value,
									label: updatedCategory.label
								});
								const id = `category_ids:=${updatedCategory.value}`;
								apply({ ...otherFilters, [id]: true });
								track('Product category', updatedCategory.label);
							}
						}}
					/>
				</InputGroup>

				{lastMileChannels?.length > 0 && (
					<InputGroup
						label={
							<div className={styles.menuCategoriesContainer}>
								<span>Menu categories</span>
								<div className={styles.dropdownIconContainer}>
									{lastMileChannels?.map((channelInfo) => {
										return (
											<li key={channelInfo.id}>
												<div
													className={styles.dropdownIcon}
													style={{ backgroundImage: `url(${channelInfo?.iconUrl})` }}
												/>
											</li>
										);
									})}
								</div>
							</div>
						}
					>
						<DeliveryCategoriesSelect
							value={selectedDeliveryCategories}
							onChange={(updatedCategories) => {
								setSelectedDeliveryCategories(updatedCategories);

								const otherFilters = Object.entries(filters).reduce((acc, [key, value]) => {
									if (key.startsWith('delivery_categories:=')) return acc;
									return { ...acc, [key]: value };
								}, {});

								if (!updatedCategories?.length) {
									apply(otherFilters);
								} else {
									const deliveryCategoryFilters = updatedCategories.reduce(
										(acc, category) => ({
											...acc,
											[`delivery_categories:=${category}`]: true
										}),
										{}
									);
									apply({ ...otherFilters, ...deliveryCategoryFilters });

									track('Menu categories', updatedCategories.map((category) => category.label).join(', '));
								}
							}}
						/>
					</InputGroup>
				)}
			</Drawer>
		</>
	);
};

export default InventoryFilters;
